import { registerIndicator, type IndicatorDrawParams, type KLineData } from 'klinecharts'
import { registerOnce } from './registrationUtils'
import { isRegularSession } from './marketHours'

export interface PreviousCloseConfig {
  color: string
  timezone: string
}

const INDICATOR_NAME = 'PREVIOUS_CLOSE'

// Cache DateTimeFormat per timezone
const dtfCache = new Map<string, Intl.DateTimeFormat>()

function getDateStr(timestamp: number, tz: string): string {
  let dtf = dtfCache.get(tz)
  if (!dtf) {
    dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    dtfCache.set(tz, dtf)
  }
  return dtf.format(new Date(timestamp))
}

/**
 * Each day's REGULAR-HOURS close: the close of the last bar within 09:30-16:00 ET (the 16:00
 * close), NOT the after-hours close. Falls back to the day's last bar when there's no RTH bar
 * (daily bars, whose close is already the RTH close). Chronological day order.
 */
export function buildDayCloses(
  kLineDataList: KLineData[],
  tz: string,
): { date: string; close: number }[] {
  const byDay = new Map<string, KLineData[]>()
  for (const bar of kLineDataList) {
    if (!bar) continue
    const d = getDateStr(bar.timestamp, tz)
    const arr = byDay.get(d)
    if (arr) arr.push(bar)
    else byDay.set(d, [bar])
  }

  const out: { date: string; close: number }[] = []
  for (const [d, dayBars] of byDay) {
    let close: number | undefined
    for (const b of dayBars) if (isRegularSession(b.timestamp)) close = b.close // last RTH bar
    if (close == null && dayBars.length > 0) close = dayBars[dayBars.length - 1].close
    if (close != null) out.push({ date: d, close })
  }
  return out
}

export const registerPreviousCloseIndicator = registerOnce(() => {
  registerIndicator({
    name: INDICATOR_NAME,
    shortName: '',
    figures: [],
    calc: (dataList: KLineData[]) => {
      return dataList.map(() => ({}))
    },
    draw: (params: IndicatorDrawParams) => {
      const { ctx, kLineDataList, visibleRange, xAxis, yAxis, indicator } = params
      const config = indicator.extendData as PreviousCloseConfig | undefined
      if (!config) return false

      const tz = config.timezone || 'America/New_York'
      const { from, to } = visibleRange
      if (from >= to || kLineDataList.length === 0) return false

      // Each day's REGULAR-HOURS close (16:00 ET), scanned across the whole series so a day
      // at the left edge still sees its prior day.
      const dayCloses = buildDayCloses(kLineDataList, tz)
      if (dayCloses.length < 2) return false

      // Build a map: date → previous day's close
      const prevCloseByDate = new Map<string, number>()
      for (let i = 1; i < dayCloses.length; i++) {
        prevCloseByDate.set(dayCloses[i].date, dayCloses[i - 1].close)
      }

      // Now draw lines for each day span in the visible range
      ctx.save()
      ctx.strokeStyle = config.color
      ctx.lineWidth = 1
      ctx.setLineDash([4, 4])

      let currentDate = ''
      let dayStartIdx = from
      let dayPrevClose: number | undefined

      for (let i = from; i <= to; i++) {
        const bar = i < to ? kLineDataList[i] : null
        const d = bar ? getDateStr(bar.timestamp, tz) : ''

        if (d !== currentDate || i === to) {
          // Draw the line for the completed day
          if (currentDate && dayPrevClose != null) {
            const x1 = xAxis.convertToPixel(dayStartIdx)
            const x2 = xAxis.convertToPixel(i - 1)
            const y = yAxis.convertToPixel(dayPrevClose)
            if (isFinite(y) && isFinite(x1) && isFinite(x2)) {
              ctx.beginPath()
              ctx.moveTo(x1, y)
              ctx.lineTo(x2, y)
              ctx.stroke()
            }
          }
          currentDate = d
          dayStartIdx = i
          dayPrevClose = prevCloseByDate.get(d)
        }
      }

      ctx.restore()
      return false
    },
  })
})

export const PREVIOUS_CLOSE_NAME = INDICATOR_NAME
